// Typed client for the xnapz REST sidecar (pkg/api).

export interface Health {
  status: string
  inbox: string
  pending?: number
  dlq_depth?: number
}

export interface Envelope {
  envelope_id: string
  ts: string
  sender: string
  kind: string
  severity: string
  recipient?: string
}

export interface Skill {
  name: string
  category?: string
  concerns?: string[]
  blocking?: boolean
  description?: string
  triggers?: string[]
  tier?: number
  source?: string
}

// ---- unified skill taxonomy ----

export type TaxAxis = 'by-trade' | 'by-domain' | 'by-phase'
export type TaxExecutor = 'ai' | 'human'

export interface TaxCategory {
  id: string
  name: string
  axis: TaxAxis
  parents?: string[]
  estimated?: boolean
}
export interface TaxSkill {
  id: string
  name: string
  executor: TaxExecutor
  categories?: string[]
  keywords?: string[]
  description?: string
  aliases?: string[]
  base_success?: number
  estimated?: boolean
}
export interface TaxDiscipline {
  id: string
  name: string
  executor: TaxExecutor
  parents?: string[]
  categories?: string[]
  skills?: string[]
  estimated?: boolean
}
export interface Taxonomy {
  skills: TaxSkill[]
  categories: TaxCategory[]
  disciplines: TaxDiscipline[]
}
export interface DisciplineCoverage {
  skills: string[]
  inboxes: string[]
}
export interface DisciplineRoute {
  skill: string
  inbox: string
  disciplines: string[]
  subscribers: string[]
}

export interface Kind {
  name: string
  version?: number
  status?: string
  owner?: string
  description?: string
  blast_radius?: string
  sensitivity?: string
  aliases?: string[]
}

export interface Plan {
  id: string
  parent_id?: string
  name: string
  status: string
  score?: number
  source_ref?: string
}

export interface SkillProgress {
  name: string
  status: string
  duration_ns?: number
}

export interface OrchestrationRun {
  run_id: string
  skills: Record<string, SkillProgress>
}

export interface Schedule {
  name: string
  skill: string
  interval: number // nanoseconds
  last_run?: string
}

export interface RouteRule {
  kind: string
  inbox?: string
  queue?: string
  preference?: string
}

export interface TelemetryEvent {
  ts: string
  component: string
  operation: string
  type: string
  status?: string
  level?: string
  duration_ns?: number
  err?: string
  trace_id?: string
  fields?: Record<string, unknown>
}

export interface Edge {
  from: string
  to: string
  count: number
}

export interface Connections {
  edges: Edge[]
}

export interface BudgetProfile {
  max_depth: number
  max_executions: number
  max_turns: number
}

export interface BudgetStat {
  task_type: string
  samples: number
  p95_depth: number
  p95_exec: number
  adapting: boolean
  recommended: BudgetProfile
}

export interface Budgets {
  profiles: Record<string, BudgetProfile>
  stats: BudgetStat[]
}

export interface InboxCount {
  inbox: string
  count: number
}

export interface CategoryRollup {
  category: string
  total: number
  inboxes: InboxCount[]
}

export interface Inboxes {
  categories: CategoryRollup[]
}

export interface PlanTask {
  id: string
  title: string
  description?: string
  depends_on: string[]
  assignee: 'ai' | 'human'
  skill?: string
  required_skill?: string // resolved unified-taxonomy skill id (AI or manual)
  owner?: string
  team?: string
  inbox?: string
  success_probability: number
  status: string
  tier: number
  pinned?: boolean
  cache_ok?: boolean
  output?: string
  run_note?: string
  estimate_hours?: number // manual projects: on-site hours (feeds the bid)
  materials?: TaskMaterial[]
}

export interface TaskMaterial {
  name: string
  qty: number
  unit?: string
  spec?: string // brand/grade/model — contracts specify WHAT is installed
  spec_options?: string[] // cheap→premium ladder, aligned with the 5-tier lever
  description?: string // user's own contract wording for this line
  pinned?: boolean // user-edited — the refiner never touches it
  qty_pinned?: boolean // quantity user-frozen too
}

// ---- bid pipeline (manual projects) ----

export interface RateBand {
  low: number
  avg: number
  high: number
  source: string // llm | manual
  region?: string
}

export interface BidMaterial {
  name: string
  qty: number
  unit?: string
  spec?: string // brand/grade/model — flows into the printed contract
  no_auto_price?: boolean // vendor-quoted / required placeholder — refuse to guess
  spec_options?: string[] // cheap→premium ladder (index+1 = tier)
  spec_choice?: number // 1..5 per-line grade override
  description?: string // custom contract wording for this line
  unit_price: number
  price_source?: string // llm | manual | override | allowance | quote-required | ''
  override_price?: number
  qty_basis?: string // "computed: 8×16ft × 1.5ft…" | "unverified — …"
  subtotal: number
}

export interface BidItem {
  task_id: string
  title: string
  trade: string
  hours: number
  rate: number
  rate_source?: string
  override_rate?: number
  override_hours?: number
  hours_basis?: string
  labor_basis?: string // structural basis (corporate): per_sqft/per_lf/…
  basis_vendor_id?: string // vendor this line priced off (attribution + creep link)
  basis_estimated?: boolean // priced off a synthesized/estimated source
  basis_creeping?: boolean // the sourced vendor's rate is climbing (cost-creep flag)
  materials?: BidMaterial[]
  labor_subtotal: number
  materials_subtotal: number
  subtotal: number
}

// ---- bid review (compare/merge): a regenerate is a proposal, not a landing ----

export type BidChangeKind = 'added' | 'removed' | 'changed' | 'renamed'

export interface FieldDiff {
  path: string // accept/reject handle
  field: string
  old?: unknown
  new?: unknown
}

export interface MaterialDiff {
  path: string
  kind: BidChangeKind
  key: string
  old?: BidMaterial
  new?: BidMaterial
  fields?: FieldDiff[]
}

export interface ItemDiff {
  path: string
  kind: BidChangeKind
  task_id: string
  title: string
  old?: BidItem
  new?: BidItem
  fields?: FieldDiff[] // empty + materials present = neutral container, never highlighted itself
  materials?: MaterialDiff[]
}

export interface BidDelta {
  doc?: FieldDiff[]
  items?: ItemDiff[]
  old_total: number
  new_total: number
  changes: number
}

export interface BidPreview {
  plan_id: string
  base_hash: string
  current: Bid
  proposed: Bid
  delta: BidDelta
  created_at: string
}

// ---- corporate vendor network ----

export type PricingBasis =
  | 'hourly' | 'per_sqft' | 'per_lf' | 'per_square' | 'per_unit' | 'per_job' | 'per_cuyd' | 'per_acre'

export interface PricePoint {
  id?: string
  trade?: string
  region?: string
  basis?: PricingBasis
  rate: number
  quantity?: number
  at?: string // RFC3339
  source: string // estimated | quoted | actual
  won?: boolean
}

export interface Vendor {
  id: string
  company_id?: string
  name: string
  kind: string // subcontractor | supplier | crew
  trade: string
  regions?: string[]
  tiles?: string[]
  basis?: PricingBasis
  low?: number
  avg?: number
  high?: number
  reliability?: string
  estimated?: boolean
  bids_won?: number
  last_won_at?: string
  history?: PricePoint[]
  updated_at?: string
}

export interface CreepSummary {
  points: number
  current_low: number
  current_avg: number
  current_high: number
  trend: 'up' | 'down' | 'flat'
  pct_change: number
  slope_per_year?: number
  creeping: boolean
  estimated?: boolean
}

export interface VendorCreepRow {
  vendor: Vendor
  summary: CreepSummary
  series: PricePoint[]
  unit?: string
}

export interface Bid {
  plan_id: string
  status: 'draft' | 'proposed'
  region: string
  tier: number
  labor_tier?: number // labor rate axis (0 = follow material tier)
  items: BidItem[]
  labor_total: number
  materials_total: number
  overhead_profit: number
  overhead_profit_rate?: number
  total: number
  generated_at: string
  proposed_at?: string
  plan_revision?: number // staleness: compare against board.revision
  anchor_low?: number // top-down ROM band (expected all-in) for a job this size
  anchor_high?: number
  anchor_label?: string
  sanity_flag?: string // set when total is far outside the anchor band (likely under/over-scoped)
  component_warnings?: string[] // required components present but priced absurdly low
  pricing_gaps?: number // unpriced material lines + unrated labor hours — total is NOT the whole job
  labor_norm_low?: number // crew-hours band typical for a job this size (0 = no norm)
  labor_norm_high?: number
}

export interface RethinkJob {
  running: boolean
  phase?: string
  started?: string
  error?: string
  done: boolean
  stopped?: boolean
}

export interface RevEntry {
  event_id: string
  revision: number
  at: string
  summary: string
}

export interface QueueItem {
  plan_id: string
  plan_title: string
  plan_status: string
  task_id: string
  title: string
  description?: string
  status: string
  owner?: string
  claimed_at?: string
  done_at?: string
  created_at?: string
  ready: boolean
  depends_on: string[]
  plan_priority: number
  blocks: number
}

export interface WorkQueue {
  inbox: string
  unclaimed: number
  active_claims: number
  done: number
  subscribers?: string[]
  items: QueueItem[]
}

export interface PlanEvent {
  at: string
  kind: string
  summary: string
  actor?: string
  detail?: unknown
}

export interface AuditTrail {
  plan_id: string
  plan_title: string
  events: PlanEvent[] | null
  runs: RunMeta[] | null
}

export interface DecideItem {
  kind: string
  urgency: string
  plan_id: string
  plan_title: string
  plan_priority: number
  question_id?: string
  task_id?: string
  run_id?: string
  owner?: string
  summary: string
  detail?: string
  since?: string
}

export interface StaleItem {
  category: string
  plan_id?: string
  plan_title?: string
  task_id?: string
  run_id?: string
  inbox?: string
  owner?: string
  summary: string
  since?: string
}

export interface StaleReport {
  generated_at: string
  scheduler_last_tick?: string
  scheduler_alarm: boolean
  items: StaleItem[] | null
  category_counts: Record<string, number>
}

export interface DesignQuestion {
  id: string
  text: string
  why?: string
  critical: boolean
  answer?: string
  recommended?: string // generator's suggested answer; auto-answer uses it (non-critical only)
  topic?: string // region|size|scope — bid-intake gates key off these
}

export interface PlanBoard {
  id?: string
  source_ref?: string
  title: string
  description?: string
  target_repo?: string
  status?: string // ''/'draft' | 'finalized'
  revision?: number // bumps on every real plan change (history + staleness)
  structured_rev?: number // revision at the last decompose/rethink
  design_doc_rev?: number // revision the narrative was written at
  project_type?: string // build | workflow | manual (detected; user-overridable chip)
  materials_refine_fp?: string // fingerprint of the last materials-refine pass (staleness check)
  // Auto-advance bookkeeping: the revision (or input fingerprint) each
  // automatic pass last ran at, so the daemon can tell "already done at this
  // revision" from "stale". The UI never renders these, but they are part of
  // the board the API returns.
  auto_research_rev?: number
  auto_deepen_rev?: number
  auto_bid_rev?: number
  auto_bid_fp?: string
  mode?: string // homeowner | corporate (pricing basis)
  company_id?: string
  vendor_pricing?: boolean // corporate: price subbed trades off the in-region vendor network
  design_doc?: string // generated narrative (markdown)
  design_doc_at?: string
  questions?: DesignQuestion[] // open questions; criticals gate a run
  quality_tier: number
  priority?: number
  updated_at?: string // last real change (RFC3339) — drives the portfolio's recency sort
  tasks: PlanTask[]
  schedule?: ScheduleSpec // set when the plan is scheduled to run (absent = run-once/manual)
}

export interface ScheduleSpec {
  kind: 'once' | 'every'
  every_minutes?: number // recurring cadence (kind==='every')
  at?: string // RFC3339 fire time (kind==='once')
  next_run?: string // RFC3339 next fire the scheduler compares against
  last_run?: string // RFC3339 last time the scheduler fired this plan
}

export interface SkillBundle {
  name: string
  domain: string
  base_success: number
  keywords?: string[] // matcher terms — the router's routing vocabulary
}

export interface OwnerInfo {
  name: string
  team: string
}

export interface SkillStat {
  skill: string
  success: number
  fail: number
  samples: number
  rate: number
}

export interface PlanMeta {
  id: string
  title: string
  client?: string
  task_count: number
  updated_at?: string
  active: boolean
}

export interface TaskRun {
  task_id: string
  title: string
  skill?: string
  execution_cached?: boolean
  status: string
  started_at?: string
  ended_at?: string
  duration_s?: number
  note?: string
  output?: string
  diff?: string
  diff_stat?: string
  step_state?: string // step-mode gate: awaiting|approved|rejected
  step_by?: string
}

export interface RunReview {
  disposition: string
  at: string
  note?: string
  branch?: string
}

export interface Run {
  id: string
  plan_id: string
  plan_title: string
  root_id?: string
  trigger?: { kind: string; principal?: string; detail?: string }
  review?: RunReview
  status: string
  started_at: string
  finished_at?: string
  duration_s?: number
  tasks: TaskRun[]
  passed: number
  failed: number
  error?: string
  target_repo?: string
  diff?: string
  diff_stat?: string
  mode?: string // "step" = manual mode (serial, per-task gate)
}

export interface RunMeta {
  id: string
  plan_id: string
  plan_title: string
  status: string
  started_at: string
  finished_at?: string
  duration_s?: number
  total: number
  passed: number
  failed: number
}

export interface PlanSnapshot {
  board: PlanBoard
  plans: PlanMeta[]
  skills: SkillBundle[]
  owners: OwnerInfo[]
  calibration: SkillStat[]
  decompose_estimate_seconds?: number
  active_run_id?: string
}

export interface PlanOp {
  kind: string
  task_id?: string
  with?: string[]
  title?: string
  value?: string
  tier?: number
  success?: boolean
  // Extended vocabulary (chat-as-universal-editor): plan-level + cost ops.
  company?: string
  rate?: number
  hours?: number
  spec?: number
  price?: number
  qty?: number
  unit?: string
  desc?: string
  confirm?: boolean
}

// ActionResult is one change a chat message applied (a "changed:" chip).
export interface ActionResult {
  kind: string
  summary: string
}

// PendingAction is an expensive/irreversible action a chat message proposed but did
// not apply. Sync actions carry `op` (replay via chatConfirm); async ones carry an
// `endpoint` the frontend kicks (e.g. the existing rethink job) + a `description`.
export interface PendingAction {
  kind: string
  summary: string
  op?: PlanOp
  endpoint?: string
  description?: string
}

export interface ChatResult {
  reply: string
  snapshot: PlanSnapshot
  applied?: ActionResult[]
  pending?: PendingAction[]
}

// EstimateRecord is one committed bid prediction from the estimate ledger,
// optionally reconciled with what the job really cost.
export interface EstimateRecord {
  plan_id: string
  plan_title?: string
  region: string
  tier: number
  expected: number
  low_total: number
  high_total: number
  labor: number
  materials: number
  sources?: Record<string, number>
  proposed_at: string
  actual?: number
  actual_at?: string
  actual_note?: string
}

// AccuracyReport aggregates reconciled records into the estimator's metrics.
export interface AccuracyReport {
  proposed: number
  reconciled: number
  mape: number
  mean_signed: number
  coverage: number
  records?: EstimateRecord[]
}

// BusinessProfile is the workspace letterhead — the identity proposals go out
// under (single-tenant: one instance = one company).
export interface BusinessProfile {
  name?: string
  tagline?: string
  license_no?: string
  phone?: string
  email?: string
  address?: string
  logo_data_uri?: string
  terms?: string
}

// NextAction is one ranked step the assistant surfaces as a priority card. The
// backend marks the single top pending step `recommended`; a `blocked` step is
// shown disabled with `blocked_why`. `endpoint` names a frontend trigger (answer|
// research|deepen|finalize|run|schedule); sync bid ops carry `op` (chatConfirm).
export interface NextAction {
  id: string
  label: string
  cta: string
  why?: string
  available: boolean
  recommended?: boolean
  blocked?: boolean
  blocked_why?: string
  endpoint?: string
  op?: PlanOp
  count?: number
  /** What changed since the plan was last structured — present only on the
   *  restructure action, and only when the revision log could be read
   *  honestly. Absent means "we cannot say", NOT "nothing changed". */
  changes?: PlanChange[]
}

/** One revision since the last restructure, as the operator would recognise it.
 *  `text` comes from the revision log verbatim — it is not generated prose. */
export interface PlanChange {
  kind: 'answer' | 'edit'
  text: string
  at?: string
}

// Readiness is the typed, ranked "what's next" for a plan. stage/summary are the
// IMPLIED stage (a subtitle, not a bar).
export interface Readiness {
  plan_id: string
  stage: string
  summary: string
  actions: NextAction[]
  open_criticals: number
  open_questions: number
  critical_texts?: string[]
  missing_intake?: string[]
  unstaffed?: string[]
  bid_status?: string
  bid_total?: number
  bid_tier?: number
  drift: number
  finalized: boolean
  access?: AccessCheck[]
  auto_next?: { stage: string; tier?: string; fire_at: string }
  auto_running?: { stage: string; started_at: string; elapsed_sec: number }
  auto_idle_reason?: string
  // Where this job sits on the concept→close-out spine, and the outward half of
  // it: what the client did with the proposal.
  job_stage?: JobStageId
  client_status?: 'open' | 'accepted' | 'declined'
  client_name?: string
  client_at?: string
  client_seen?: boolean
}

// The spine, in order. Derived server-side from state that already exists, so a
// job's stage can never disagree with what the plan actually is.
export type JobStageId =
  | 'concept' | 'scoping' | 'pricing' | 'proposed' | 'accepted' | 'building' | 'closing'

export const JOB_STAGES: { id: JobStageId; label: string; note: string }[] = [
  { id: 'concept', label: 'Concept', note: 'describe the job' },
  { id: 'scoping', label: 'Scoping', note: 'answer what the price depends on' },
  { id: 'pricing', label: 'Pricing', note: 'build the estimate' },
  { id: 'proposed', label: 'Proposed', note: 'sent to the client' },
  { id: 'accepted', label: 'Accepted', note: 'client said yes' },
  { id: 'building', label: 'Building', note: 'crew is on it' },
  { id: 'closing', label: 'Close-out', note: 'actuals and accuracy' },
]

// JobSummary is one job's row in the portfolio — the same spine as the rail,
// rendered for every job at once so a contractor running fifteen of them can
// triage without opening each one.
export interface JobSummary {
  id: string
  title: string
  client?: string
  stage: JobStageId
  stage_label: string
  waiting: string
  tasks: number
  bid_total?: number
  bid_status?: string
  client_status?: string
  finalized: boolean
  updated_at?: string
  priority?: number
}

// ModelOption / ModelStep: the per-step model catalog (which model runs each
// lifecycle step, the options, the recommended one).
export interface ModelOption {
  model: string
  label: string
  recommended?: boolean
  note?: string
}
export interface ModelStep {
  step: string // '' = informational row (not selectable)
  label: string
  description: string
  current: string
  overridden?: boolean
  options?: ModelOption[]
}

// AccessCheck is one probed run-path capability (W2.7 access audit).
export interface AccessCheck {
  id: string
  label: string // a noun — it also reads inside "Provision first: …"
  status: string // ok|missing|degraded|gated
  detail?: string // what to provision, in plain language
  effect?: string // what the operator loses meanwhile — the chip's "so what"
}

export interface TaskResult {
  task_id: string
  title: string
  skill?: string
  status: string
  note?: string
  output?: string
}

export interface RunReport {
  run_id: string
  results: TaskResult[]
  recorded: number
  error?: string
}

export interface RunResult {
  report: RunReport
  snapshot: PlanSnapshot
}

export interface KindNode {
  kind: string
  subscribed: boolean
}

export interface SubCategory {
  name: string
  subscribed: 'all' | 'partial' | 'none'
  kinds: KindNode[]
}

export interface Subscriptions {
  categories: SubCategory[]
}

// A 401 is not a per-view error, it is a fact about the session: the gate in
// front of the API refused us. Every caller learns it from one place so the
// shell can put up a sign-in gate instead of each view rendering its own red
// "HTTP 401" — which is what the public story page was doing to anonymous
// readers.
export const UNAUTHENTICATED_EVENT = 'xnapz:unauthenticated'
function noteUnauthenticated() {
  window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT))
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path)
  if (!res.ok) {
    if (res.status === 401) noteUnauthenticated()
    throw new Error(`${path} → HTTP ${res.status}`)
  }
  return (await res.json()) as T
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    if (res.status === 401) noteUnauthenticated()
    // The server sends an honest reason in {error}; losing it turns every
    // refusal into an unactionable "HTTP 400".
    let reason = ''
    try {
      const body = await res.json()
      if (body && typeof body.error === 'string') reason = body.error
    } catch {
      /* non-JSON error body */
    }
    throw new Error(reason || `${path} → HTTP ${res.status}`)
  }
  return (await res.json()) as T
}

export const api = {
  health: () => getJSON<Health>('/api/health'),
  envelopes: (spec: string) =>
    getJSON<Envelope[] | null>(`/api/envelopes?spec=${encodeURIComponent(spec)}`),
  skills: () => getJSON<Skill[] | null>('/api/skills'),
  kinds: () => getJSON<Kind[] | null>('/api/kinds'),
  plans: () => getJSON<Plan[] | null>('/api/plans'),
  jobs: () => getJSON<{ jobs: JobSummary[] | null }>('/api/planning/jobs'),
  orchestration: (id: string) =>
    getJSON<OrchestrationRun>(`/api/orchestrations/${encodeURIComponent(id)}`),
  schedules: () => getJSON<Schedule[] | null>('/api/schedules'),
  routes: () => getJSON<RouteRule[] | null>('/api/routing/rules'),
  telemetry: (limit = 100) =>
    getJSON<TelemetryEvent[] | null>(`/api/telemetry?limit=${limit}`),
  connections: () => getJSON<Connections>('/api/connections'),
  budgets: () => getJSON<Budgets>('/api/budgets'),
  inboxes: () => getJSON<Inboxes>('/api/inboxes'),
  planning: () => getJSON<PlanSnapshot>('/api/planning'),
  readiness: (plan?: string) =>
    getJSON<Readiness>(`/api/planning/readiness${plan ? `?plan=${encodeURIComponent(plan)}` : ''}`),
  accuracy: () => getJSON<AccuracyReport>('/api/planning/accuracy'),
  recordActual: (id: string, amount: number, note: string) =>
    postJSON<{ record: EstimateRecord }>('/api/planning/ledger/actual', { id, amount, note }),
  business: () => getJSON<{ business: BusinessProfile }>('/api/planning/business'),
  bidShare: (id: string, html: string) =>
    postJSON<{ token: string; path: string }>('/api/planning/bid/share', { id, html }),
  setBusiness: (p: BusinessProfile) =>
    postJSON<{ business: BusinessProfile }>('/api/planning/business', p),
  planPriority: (id: string, priority: number) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/priority', { id, priority }),
  planType: (id: string, type: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/type', { id, type }),
  planMode: (id: string, mode: string, company_id?: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/mode', { id, mode, company_id }),
  planHistory: (plan: string) => getJSON<{ history: RevEntry[] }>(`/api/planning/history/${plan}`),
  planRevert: (id: string, event_id: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/revert', { id, event_id }),
  planRethink: (id: string, narrate = false) => postJSON<{ job: RethinkJob }>('/api/planning/plans/rethink', { id, narrate }),
  planResearch: (id: string) => postJSON<{ snapshot: PlanSnapshot; filled: number; remaining: number }>('/api/planning/plans/research', { id }),
  rethinkStatus: (plan: string) => getJSON<{ job: RethinkJob }>(`/api/planning/plans/rethink/${plan}`),
  rethinkStop: (plan: string) => postJSON<{ job: RethinkJob }>(`/api/planning/plans/rethink/${plan}/stop`, {}),
  bidGenerate: (id: string) => postJSON<{ bid: Bid }>('/api/planning/bid/generate', { id }),
  bidPreview: (id: string) => postJSON<{ preview: BidPreview }>('/api/planning/bid/preview', { id }),
  bidPreviewGet: (plan: string) =>
    getJSON<{ preview: BidPreview | null }>(`/api/planning/bid/preview/${plan}`),
  bidPreviewApply: (id: string, accept: string[]) =>
    postJSON<{ bid: Bid }>('/api/planning/bid/preview/apply', { id, accept }),
  bidPreviewDiscard: (id: string) =>
    postJSON<{ ok: boolean }>('/api/planning/bid/preview/discard', { id }),
  bidGet: (plan: string) => getJSON<{ bid: Bid }>(`/api/planning/bid/${plan}`),
  bidTier: (id: string, tier: number) => postJSON<{ bid: Bid }>('/api/planning/bid/tier', { id, tier }),
  bidFitHours: (id: string) => postJSON<{ bid: Bid }>('/api/planning/bid/fit-hours', { id }),
  autoAdvanceNow: (id: string) => postJSON<{ stage: string }>('/api/planning/auto-advance', { id }),
  modelPrefs: () => getJSON<{ steps: ModelStep[] }>('/api/planning/model-prefs'),
  setModelPref: (step: string, model: string) =>
    postJSON<{ steps: ModelStep[] }>('/api/planning/model-prefs', { step, model }),
  bidLaborTier: (id: string, tier: number) => postJSON<{ bid: Bid }>('/api/planning/bid/labor-tier', { id, tier }),
  bidItem: (body: {
    id: string
    task_id: string
    rate?: number
    hours?: number
    material?: string
    material_price?: number
    spec_choice?: number
    description?: string
  }) => postJSON<{ bid: Bid }>('/api/planning/bid/item', body),
  bidPropose: (id: string) => postJSON<{ bid: Bid }>('/api/planning/bid/propose', { id }),
  companyNetwork: (companyId: string, region?: string) =>
    getJSON<{ region: string; vendors: VendorCreepRow[] }>(
      `/api/planning/companies/${encodeURIComponent(companyId)}/network${region ? `?region=${encodeURIComponent(region)}` : ''}`,
    ),
  vendorUpsert: (companyId: string, vendor: Vendor) =>
    postJSON<{ vendor: Vendor }>(`/api/planning/companies/${encodeURIComponent(companyId)}/vendors`, vendor),
  vendorQuote: (companyId: string, vendorId: string, point: PricePoint) =>
    postJSON<{ vendor: Vendor }>(`/api/planning/companies/${encodeURIComponent(companyId)}/vendors/quote`, { vendor_id: vendorId, point }),
  setVendorPricing: (id: string, enabled: boolean) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/vendor-pricing', { id, enabled }),
  taxonomy: () => getJSON<Taxonomy>('/api/taxonomy'),
  disciplineCoverage: (id: string) =>
    getJSON<DisciplineCoverage>(`/api/taxonomy/discipline/${encodeURIComponent(id)}/coverage`),
  disciplineSubscribe: (discipline: string, person: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/disciplines/subscribe', { discipline, person }),
  disciplineUnsubscribe: (discipline: string, person: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/disciplines/unsubscribe', { discipline, person }),
  disciplineRoutes: () => getJSON<{ routes: DisciplineRoute[] }>('/api/routing/disciplines'),
  rates: () => getJSON<{ rates: Record<string, RateBand> }>('/api/planning/rates'),
  rateSet: (key: string, low: number, avg: number, high: number) =>
    postJSON<{ rates: Record<string, RateBand> }>('/api/planning/rates', { key, low, avg, high }),
  planningOp: (op: PlanOp) =>
    postJSON<{ reply: string; snapshot: PlanSnapshot }>('/api/planning/op', op),
  planningChat: (message: string) => postJSON<ChatResult>('/api/planning/chat', { message }),
  chatConfirm: (op: PlanOp) => postJSON<ChatResult>('/api/planning/chat/confirm', { op }),
  planningRun: () => postJSON<RunResult>('/api/planning/run', {}),
  // Streaming run: POSTs and reads SSE, invoking onTask as each task starts/finishes,
  // then resolves with the final report + snapshot.
  planningRunStream: async (
    onTask: (ev: { task_id: string; title: string; skill?: string; status: string }) => void,
  ): Promise<RunResult> => {
    const res = await fetch('/api/planning/run/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
    if (!res.ok || !res.body) throw new Error(`run failed (${res.status})`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    let result: RunResult | null = null
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let idx: number
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        let ev = 'message'
        let data = ''
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) ev = line.slice(6).trim()
          else if (line.startsWith('data:')) data += line.slice(5).trim()
        }
        if (!data) continue
        const parsed = JSON.parse(data)
        if (ev === 'task') onTask(parsed)
        else if (ev === 'result') result = parsed
      }
    }
    if (!result) throw new Error('run ended without a result')
    return result
  },
  runReview: (id: string, disposition: string, note: string) =>
    postJSON<{ run: Run }>(`/api/planning/runs/${encodeURIComponent(id)}/review`, { disposition, note }),
  runApply: (id: string, branch: string) =>
    postJSON<{ run: Run }>(`/api/planning/runs/${encodeURIComponent(id)}/apply`, { branch }),
  runRepair: (id: string, task_id: string, note: string) =>
    postJSON<{ repairing: boolean }>(`/api/planning/runs/${encodeURIComponent(id)}/repair`, { task_id, note }),
  auditTrail: (planId: string) => getJSON<AuditTrail>(`/api/planning/audit/${encodeURIComponent(planId)}`),
  decideItems: () => getJSON<{ items: DecideItem[] }>('/api/planning/decide'),
  staleReport: () => getJSON<StaleReport>('/api/planning/stale'),
  workQueues: () => getJSON<{ queues: WorkQueue[] | null }>('/api/planning/queues'),
  queueClaim: (plan_id: string, task_id: string, person: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/queues/claim', { plan_id, task_id, person }),
  queueComplete: (plan_id: string, task_id: string, note: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/queues/complete', { plan_id, task_id, note }),
  queueAccept: (plan_id: string, task_id: string, person: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/queues/accept', { plan_id, task_id, person }),
  queueBounce: (plan_id: string, task_id: string, note: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/queues/bounce', { plan_id, task_id, note }),
  queueSubscribe: (inbox: string, person: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/queues/subscribe', { inbox, person }),
  queueUnsubscribe: (inbox: string, person: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/queues/unsubscribe', { inbox, person }),
  queueRelease: (plan_id: string, task_id: string) =>
    postJSON<{ queues: WorkQueue[] }>('/api/planning/queues/release', { plan_id, task_id }),
  planningDecompose: (title: string, description: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/decompose', { title, description }),
  // Streaming decompose: POSTs and reads SSE, invoking onPhase as the model works
  // through requirements→design→tasks, then resolves with the final snapshot.
  planningDecomposeStream: async (
    title: string,
    description: string,
    onPhase: (phase: string, status: string) => void,
  ): Promise<{ snapshot: PlanSnapshot }> => {
    const res = await fetch('/api/planning/decompose/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, description }),
    })
    if (!res.ok || !res.body) throw new Error(`decompose failed (${res.status})`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    let result: { snapshot: PlanSnapshot } | null = null
    let errMsg: string | null = null
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let idx: number
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        let ev = 'message'
        let data = ''
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) ev = line.slice(6).trim()
          else if (line.startsWith('data:')) data += line.slice(5).trim()
        }
        if (!data) continue
        const parsed = JSON.parse(data)
        if (ev === 'phase') onPhase(parsed.phase, parsed.status)
        else if (ev === 'result') result = parsed
        else if (ev === 'error') errMsg = parsed.error
      }
    }
    if (errMsg) throw new Error(errMsg)
    if (!result) throw new Error('decompose ended without a result')
    return result
  },
  runStart: (mode?: string) => postJSON<{ run_id: string }>('/api/planning/runs', mode ? { mode } : {}),
  runStep: (id: string, taskId: string, choice: 'approve' | 'reject') =>
    postJSON<{ run: Run }>(`/api/planning/runs/${encodeURIComponent(id)}/step`, { task_id: taskId, choice }),
  runGet: (id: string) => getJSON<Run>(`/api/planning/runs/${encodeURIComponent(id)}`),
  runStop: (id: string) =>
    postJSON<{ stopped: boolean }>(`/api/planning/runs/${encodeURIComponent(id)}/stop`, {}),
  runHistory: (plan?: string) =>
    getJSON<RunMeta[] | null>(`/api/planning/runs${plan ? `?plan=${encodeURIComponent(plan)}` : ''}`),
  planSwitch: (id: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/switch', { id }),
  planRename: (id: string, title: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/rename', { id, title }),
  planSetClient: (id: string, client: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/client', { id, client }),
  planDelete: (id: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/delete', { id }),
  planSetTarget: (id: string, target_repo: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/target', { id, target_repo }),
  planSetStatus: (id: string, status: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/status', { id, status }),
  planSchedule: (id: string, spec: ScheduleSpec) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/schedule', { id, spec }),
  planUnschedule: (id: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/plans/unschedule', { id }),
  generateDesignDoc: () =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/design-doc', {}),
  answerQuestion: (planId: string, questionId: string, answer: string) =>
    postJSON<{ snapshot: PlanSnapshot }>('/api/planning/questions/answer', {
      plan_id: planId,
      question_id: questionId,
      answer,
    }),
  // Streaming design-doc generation: POSTs and reads SSE, invoking onPhase as the author
  // moves through search→diagrams→assemble→publish, then resolves with the final snapshot.
  generateDesignDocStream: async (
    onPhase: (phase: string, status: string) => void,
  ): Promise<{ snapshot: PlanSnapshot }> => {
    const res = await fetch('/api/planning/design-doc/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
    if (!res.ok || !res.body) throw new Error(`design-doc failed (${res.status})`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    let result: { snapshot: PlanSnapshot } | null = null
    let errMsg: string | null = null
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let idx: number
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        let ev = 'message'
        let data = ''
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) ev = line.slice(6).trim()
          else if (line.startsWith('data:')) data += line.slice(5).trim()
        }
        if (!data) continue
        const parsed = JSON.parse(data)
        if (ev === 'phase') onPhase(parsed.phase, parsed.status)
        else if (ev === 'result') result = parsed
        else if (ev === 'error') errMsg = parsed.error
      }
    }
    if (errMsg) throw new Error(errMsg)
    if (!result) throw new Error('design-doc ended without a result')
    return result
  },
  subscriptions: () => getJSON<Subscriptions>('/api/subscriptions'),
  subscriptionToggle: (target: string) =>
    postJSON<Subscriptions>('/api/subscriptions/toggle', { target }),
}

// formatDuration renders a Go nanosecond duration as a compact human string.
export function formatDuration(ns: number): string {
  const sec = ns / 1e9
  if (sec < 60) return `${sec.toFixed(0)}s`
  const min = sec / 60
  if (min < 60) return `${min.toFixed(0)}m`
  const hr = min / 60
  if (hr < 24) return `${hr.toFixed(0)}h`
  return `${(hr / 24).toFixed(0)}d`
}
